import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { OwnerTypes } from "@/domain/_shared/lib/owner-types";

import { useUpdateStoredView } from "../../hooks/api/use-update-stored-view";
import { useCurrentViewContext } from "../../hooks/use-current-view-context";
import { filterDatatableStateByColumns, getCleanedDatatableState } from "../../models/datatable-state-model";
import { useDatatableContext } from "../datatable/use-datatable-context";

import { useStoredViewMenuContext } from "./stored-view-menu-context";
import { useHasUnsavedStoredView } from "./use-has-unsaved-stored-view";

export const StoredViewUnsavedButtons: FC = () => {
  const { t } = useTranslation("common");
  const { localStoredViewId } = useStoredViewMenuContext();
  const { currentActiveView, baseDatatableState, currentStoredViewDatatableState } = useCurrentViewContext();
  const { state: localDatatableState, setState: setDatatableState, columns } = useDatatableContext();
  const { updateStoredView, loading: updateStoredViewLoading } = useUpdateStoredView();

  const validColumnIdSet = useMemo(() => new Set(columns.map((c) => c.columnId)), [columns]);

  const { hasUnsavedChanges } = useHasUnsavedStoredView();

  const isSharedView =
    currentActiveView?.storedView?.ownerType === OwnerTypes.WORKSPACE ||
    currentActiveView?.storedView?.ownerType === OwnerTypes.TEAM;

  const handleReset = () => {
    if ((localStoredViewId ?? null) !== (currentActiveView?.storedViewId ?? null)) return;
    const referenceState = currentStoredViewDatatableState ?? baseDatatableState;
    setDatatableState({ state: referenceState });
  };

  const handleSaveChanges = () => {
    if ((localStoredViewId ?? null) !== (currentActiveView?.storedViewId ?? null)) return;
    if (!currentActiveView?.storedView || updateStoredViewLoading) return;
    const filteredLocalState = filterDatatableStateByColumns(localDatatableState, validColumnIdSet);
    const stateToSave = getCleanedDatatableState(filteredLocalState) ?? filteredLocalState;
    updateStoredView(
      { id: currentActiveView.storedView.id, ...stateToSave },
      {
        onSuccess: (updatedStoredView) => {
          setDatatableState({ state: stateToSave });
          toast.success(t("view.saveSuccess"), { description: t("view.saveSuccessDescription", { name: updatedStoredView.name }) });
        },
        onError: () => {
          toast.error(t("view.updateError"), { description: t("view.updateErrorDescription") });
        },
      },
    );
  };

  if (!hasUnsavedChanges) return null;

  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="xs" className="h-6 text-xs font-normal" onClick={handleReset}>
        {t("view.reset")}
      </Button>
      {localStoredViewId && (
        <Button
          variant="outline"
          size="xs"
          className="h-6 border-orange-500 text-xs font-normal text-orange-500 hover:bg-orange-500/10 hover:text-orange-500"
          onClick={handleSaveChanges}
          disabled={updateStoredViewLoading}
        >
          {isSharedView ? t("view.saveForEveryone") : t("view.saveChanges")}
        </Button>
      )}
    </div>
  );
};
